"use client";

import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Button,
  Avatar,
  IconButton,
  Chip,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import { tokens } from "../../../../../../../../../theme";

interface Speaker {
  id: number;
  name: string;
  image?: string;
  role?: string;
}

interface KeynotePanelistsSectionProps {
  speakers: Speaker[];
  isAdmin?: boolean;
  onAddPanelist?: () => void;
  onDeletePanelist?: (id: number) => void;
}

function SpeakerCard({
  speaker,
  isAdmin,
  onDelete,
}: {
  speaker: Speaker;
  isAdmin?: boolean;
  onDelete?: () => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
          "& .speaker-overlay": {
            opacity: 1,
          },
        },
      }}
    >
      {/* Image Section */}
      <Box
        sx={{
          position: "relative",
          height: 200,
          bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          background: speaker.image
            ? `url(${speaker.image})`
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {!speaker.image && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: "rgba(255,255,255,0.2)",
                fontSize: 32,
              }}
            >
              {speaker.name.charAt(0)}
            </Avatar>
          </Box>
        )}

        {/* Delete button for admin */}
        {isAdmin && onDelete && (
          <IconButton
            onClick={onDelete}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              "&:hover": {
                bgcolor: "#f44336",
              },
            }}
            size="small"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}

        {/* Social overlay on hover */}
        <Box
          className="speaker-overlay"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            p: 1.5,
            background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
            display: "flex",
            justifyContent: "center",
            gap: 1,
            opacity: 0,
            transition: "opacity 0.3s ease",
          }}
        >
          <IconButton
            size="small"
            sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.2)" }}
          >
            <LinkedInIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.2)" }}
          >
            <TwitterIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Info Section */}
      <Box
        sx={{
          p: 2.5,
          background: "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {speaker.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
        >
          {speaker.role || "Panelist"}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function KeynotePanelistsSection({
  speakers,
  isAdmin = false,
  onAddPanelist,
  onDeletePanelist,
}: KeynotePanelistsSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 6 },
        bgcolor: colors.primary[900],
        borderRadius: 4,
      }}
    >
      {/* Section Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#E3F2FD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GroupsIcon sx={{ color: "#1976D2", fontSize: 22 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a1a2e" }}>
              Keynote Panelists
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: "text.secondary", ml: 7 }}>
            Meet our distinguished speakers and industry experts
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddPanelist}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              px: 3,
              py: 1.25,
              bgcolor: "#1976D2",
              fontWeight: 600,
              boxShadow: "0 4px 14px rgba(25, 118, 210, 0.4)",
              "&:hover": {
                bgcolor: "#1565C0",
                boxShadow: "0 6px 20px rgba(25, 118, 210, 0.5)",
              },
            }}
          >
            Add Panelist
          </Button>
        )}
      </Stack>

      {/* Speakers Grid */}
      {speakers.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            bgcolor: colors.primary[800],
            borderRadius: 3,
            border: "2px dashed #E0E0E0",
          }}
        >
          <GroupsIcon sx={{ fontSize: 48, color: "#BDBDBD", mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No panelists available yet.
          </Typography>
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddPanelist}
              sx={{ mt: 2, textTransform: "none", borderRadius: 2 }}
            >
              Add First Panelist
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {speakers.slice(0, 4).map((speaker) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={speaker.id}>
              <SpeakerCard
                speaker={speaker}
                isAdmin={isAdmin}
                onDelete={
                  onDeletePanelist
                    ? () => onDeletePanelist(speaker.id)
                    : undefined
                }
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* View All Link */}
      {speakers.length > 4 && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Chip
            label={`+${speakers.length - 4} more panelists`}
            sx={{
              bgcolor: "#E3F2FD",
              color: "#1976D2",
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": { bgcolor: "#BBDEFB" },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
